import { useState } from 'react';
import { payrollApi, payrollLegalApi, payrollReportsApi } from '../../api/services';
import { GENERATION_TYPES, PAY_FREQUENCIES } from '../../api/types';
import type {
  BonusLineDto, EmployeeDto, PayrollMonthlyRowDto, GenerationConceptDto, GenerationDetailDto, GenerationDto, PayrollContributionLineDto, PayrollGroupDto, PayrollLineDto,
  SaveEmployeeDto, SettlementItemDto, TimeRecordDto,
} from '../../api/types';
import {
  Alert, Badge, Button, Card, Check, Field, Form, Input, Loadable, Modal, NumberInput, PageHeader, Select, Stat, Table, Tabs, Toolbar,
  firstOfMonthIso, fmtDate, fmtMoney, money, todayIso, useAction, useAsync,
} from '../../ui/components';
import { idTypeOptions, useCountry } from '../../ui/company';
import { PageHelp } from '../../ui/help';
import { TemplateSelect } from '../../ui/pickers';
import { Link } from '../../router';

type Tab = 'payroll' | 'employees' | 'groups' | 'time' | 'bonus' | 'settlement' | 'generations' | 'monthly';

export function PayrollPage() {
  const [tab, setTab] = useState<Tab>('payroll');
  const country = useCountry();
  return (
    <>
      <PageHeader title="Planilla" subtitle={`Salarios, cargas sociales, renta, bonos de ley y liquidaciones${country ? ` según la ley de ${country.name}` : ''}, con su asiento contable.`}
        actions={<Link to="/payroll-legal">Parámetros legales</Link>} />
      <PageHelp page="payroll" />
      <Tabs<Tab> tabs={[
        { key: 'payroll', label: 'Generar planilla' }, { key: 'employees', label: 'Empleados' }, { key: 'groups', label: 'Planillas' },
        { key: 'time', label: 'Novedades' }, { key: 'bonus', label: 'Bonos de ley' }, { key: 'settlement', label: 'Liquidación' },
        { key: 'generations', label: 'Generaciones' }, { key: 'monthly', label: 'Reporte a la seguridad social' },
      ]} active={tab} onChange={setTab} />
      {tab === 'payroll' && <GenerateTab />}
      {tab === 'employees' && <EmployeesTab />}
      {tab === 'groups' && <GroupsTab />}
      {tab === 'time' && <TimeTab />}
      {tab === 'bonus' && <BonusTab />}
      {tab === 'settlement' && <SettlementTab />}
      {tab === 'generations' && <GenerationsTab />}
      {tab === 'monthly' && <MonthlyReportTab />}
    </>
  );
}

function EmployeeSelect({ value, onChange, status }: { value: number | null; onChange: (v: number) => void; status?: string }) {
  const { data } = useAsync(() => payrollApi.employees(undefined, status), [status]);
  return <Select value={value ?? ''} onChange={onChange} placeholder="Empleado" options={(data ?? []).map((e) => ({ value: e.id, label: `${e.id} · ${e.name}` }))} />;
}
function GroupSelect({ value, onChange, allowAll }: { value: number | null; onChange: (v: number) => void; allowAll?: boolean }) {
  const { data } = useAsync(() => payrollApi.groups(), []);
  return <Select value={value ?? ''} onChange={onChange} placeholder={allowAll ? 'Todas' : 'Planilla'} options={(data ?? []).filter((g) => g.status === 'A').map((g) => ({ value: g.id, label: `${g.id} · ${g.name}` }))} />;
}
const activeTone = (s: string) => (s === 'A' ? 'ok' : 'neutral') as 'ok' | 'neutral';
const pct = (v: number) => `${Number(v.toFixed(4))} %`;

/** Desglose de cargas sociales (empleado y patrono) de una línea o de los totales. */
function ContributionsTable({ rows }: { rows: PayrollContributionLineDto[] }) {
  return (
    <Table rows={rows} rowKey={(c) => `${c.appliesTo}-${c.code}`} empty="Sin cargas sociales." columns={[
      { key: 'd', title: 'Carga', render: (c) => c.description },
      { key: 'a', title: 'Paga', render: (c) => (c.appliesTo === 'EMPLEADO' ? 'Empleado' : 'Patrono') },
      { key: 'p', title: 'Tasa', align: 'right', render: (c) => pct(c.percentage) },
      money<PayrollContributionLineDto>('b', 'Base', (c) => c.base),
      money<PayrollContributionLineDto>('m', 'Monto', (c) => c.amount),
    ]} />
  );
}

// ================================================================ Empleados

function EmployeesTab() {
  const [text, setText] = useState('');
  const [status, setStatus] = useState('A');
  const list = useAsync(() => payrollApi.employees(text.trim() || undefined, status || undefined), [text, status]);
  const [editing, setEditing] = useState<EmployeeDto | 'new' | null>(null);
  return (
    <>
      <Toolbar>
        <Input value={text} onChange={setText} placeholder="Nombre o identificación" />
        <Select value={status} onChange={setStatus} placeholder="Todos" options={[{ value: 'A', label: 'Activos' }, { value: 'I', label: 'Inactivos' }]} />
        <Button kind="primary" onClick={() => setEditing('new')}>Nuevo empleado</Button>
      </Toolbar>
      <Loadable state={list}>
        {(rows) => <Table rows={rows} rowKey={(e) => e.id} onRowClick={setEditing} columns={[
          { key: 'i', title: 'Número', render: (e) => e.id },
          { key: 'n', title: 'Nombre', render: (e) => e.name },
          { key: 'd', title: 'Identificación', render: (e) => e.identification ?? '' },
          { key: 'p', title: 'Puesto', render: (e) => e.position ?? '' },
          { key: 'f', title: 'Ingreso', render: (e) => fmtDate(e.hireDate) },
          { key: 's', title: 'Salida', render: (e) => fmtDate(e.exitDate) },
          { key: 'h', title: 'Horas período', align: 'right', render: (e) => e.periodHours },
          money<EmployeeDto>('r', 'Salario hora', (e) => e.hourlyRate),
          { key: 'e', title: 'Estado', render: (e) => <Badge tone={activeTone(e.status)}>{e.status === 'A' ? 'Activo' : e.settled ? 'Liquidado' : 'Inactivo'}</Badge> },
        ]} />}
      </Loadable>
      {editing && <EmployeeForm employee={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); list.reload(); }} />}
    </>
  );
}

function EmployeeForm({ employee, onClose, onSaved }: { employee: EmployeeDto | null; onClose: () => void; onSaved: () => void }) {
  const country = useCountry();
  const d = (x?: string | null) => (x ? x.slice(0, 10) : '');
  const [v, setV] = useState<SaveEmployeeDto>(employee ? {
    name: employee.name, idType: employee.idType ?? '', identification: employee.identification ?? '', position: employee.position ?? '', email: employee.email ?? '',
    phone: employee.phone ?? '', mobile: employee.mobile ?? '', address: employee.address ?? '', birthDate: d(employee.birthDate), hireDate: d(employee.hireDate),
    exitDate: d(employee.exitDate), status: employee.status, bankCode: employee.bankCode ?? null, bankAccount: employee.bankAccount ?? '', paymentMethod: employee.paymentMethod ?? '',
    periodHours: employee.periodHours, hourlyRate: employee.hourlyRate, children: employee.children ?? 0, spouse: employee.spouse ?? false, socialSecurityNumber: employee.socialSecurityNumber ?? '',
  } : { name: '', idType: '', identification: '', position: '', email: '', phone: '', mobile: '', address: '', birthDate: '', hireDate: todayIso(), exitDate: '', status: 'A', bankCode: null, bankAccount: '', paymentMethod: '', periodHours: 0, hourlyRate: 0, children: 0, spouse: false, socialSecurityNumber: '' });
  const { run, busy, fieldErrors } = useAction();
  const n = (x?: string | null) => (x && x.trim() ? x.trim() : null);
  const balance = useAsync(() => (employee ? payrollApi.vacationBalance(employee.id) : Promise.resolve(null)), [employee?.id]);
  const taxCredits = country?.code === 'CR';
  return (
    <Modal title={employee ? `Empleado ${employee.id}` : 'Nuevo empleado'} onClose={onClose} size="lg">
      <Form onSubmit={() => run(async () => {
        const dto: SaveEmployeeDto = { ...v, name: v.name.trim(), idType: n(v.idType), identification: n(v.identification), position: n(v.position), email: n(v.email), phone: n(v.phone), mobile: n(v.mobile), address: n(v.address), birthDate: n(v.birthDate), exitDate: n(v.exitDate), bankAccount: n(v.bankAccount), paymentMethod: n(v.paymentMethod), socialSecurityNumber: n(v.socialSecurityNumber) };
        if (employee) await payrollApi.updateEmployee(employee.id, dto); else await payrollApi.createEmployee(dto);
        onSaved();
      }, { success: 'Empleado guardado.' })}>
        <div className="acc-grid-form">
          <Field label="Nombre" error={fieldErrors.Name}><Input value={v.name} onChange={(x) => setV({ ...v, name: x })} maxLength={200} required /></Field>
          <Field label="Tipo de identificación"><Select value={v.idType ?? ''} onChange={(x) => setV({ ...v, idType: x })} placeholder="Elija" options={idTypeOptions(country?.personIdTypes, v.idType)} /></Field>
          <Field label="Identificación"><Input value={v.identification ?? ''} onChange={(x) => setV({ ...v, identification: x })} maxLength={50} /></Field>
          <Field label={`N.º de seguro social (${country?.socialSecurity ?? 'seguridad social'})`} hint="Para el reporte mensual a la institución"><Input value={v.socialSecurityNumber ?? ''} onChange={(x) => setV({ ...v, socialSecurityNumber: x })} maxLength={30} /></Field>
          <Field label="Puesto"><Input value={v.position ?? ''} onChange={(x) => setV({ ...v, position: x })} maxLength={100} /></Field>
          <Field label="Correo" hint="Para enviarle su comprobante de pago"><Input value={v.email ?? ''} onChange={(x) => setV({ ...v, email: x })} maxLength={150} /></Field>
          <Field label="Teléfono"><Input value={v.phone ?? ''} onChange={(x) => setV({ ...v, phone: x })} maxLength={50} /></Field>
          <Field label="Celular"><Input value={v.mobile ?? ''} onChange={(x) => setV({ ...v, mobile: x })} maxLength={50} /></Field>
          <Field label="Nacimiento"><Input type="date" value={v.birthDate ?? ''} onChange={(x) => setV({ ...v, birthDate: x })} /></Field>
          <Field label="Ingreso" hint="Define la antigüedad (vacaciones, cesantía)"><Input type="date" value={v.hireDate} onChange={(x) => setV({ ...v, hireDate: x })} required /></Field>
          <Field label="Salida" hint="Obligatoria para inactivar o liquidar"><Input type="date" value={v.exitDate ?? ''} onChange={(x) => setV({ ...v, exitDate: x })} /></Field>
          <Field label="Estado"><Select value={v.status} onChange={(x) => setV({ ...v, status: x })} options={[{ value: 'A', label: 'Activo' }, { value: 'I', label: 'Inactivo' }]} /></Field>
          <Field label="Horas del período" hint="Horas ordinarias de cada pago (p. ej. 96 en quincena de 8 h × 12 días)"><NumberInput value={v.periodHours} onChange={(x) => setV({ ...v, periodHours: x })} min={0} /></Field>
          <Field label="Salario por hora"><NumberInput step="0.0001" value={v.hourlyRate} onChange={(x) => setV({ ...v, hourlyRate: x })} min={0} /></Field>
          {taxCredits && <Field label="Hijos" hint="Crédito fiscal del impuesto al salario"><NumberInput step="1" value={v.children} onChange={(x) => setV({ ...v, children: Math.max(0, Math.min(20, Math.round(x))) })} min={0} /></Field>}
          {taxCredits && <Field label="Cónyuge"><Check checked={v.spouse} onChange={(x) => setV({ ...v, spouse: x })} label="Tiene cónyuge (crédito fiscal)" /></Field>}
          <Field label="Cuenta bancaria"><Input value={v.bankAccount ?? ''} onChange={(x) => setV({ ...v, bankAccount: x })} maxLength={50} /></Field>
          <Field label="Forma de pago"><Input value={v.paymentMethod ?? ''} onChange={(x) => setV({ ...v, paymentMethod: x })} maxLength={50} /></Field>
        </div>
        <Field label="Dirección" wide><Input value={v.address ?? ''} onChange={(x) => setV({ ...v, address: x })} maxLength={500} /></Field>
        <p><small>Salario del período: <strong>{fmtMoney(v.periodHours * v.hourlyRate)}</strong></small></p>
        {employee && balance.data && <Alert>Vacaciones disponibles: <strong>{balance.data.available}</strong> día(s) · {balance.data.daysAccrued} días ganados en {balance.data.monthsAccrued} meses − {balance.data.adjustments} de ajustes − {balance.data.vacationRecords} registro(s) de vacaciones.</Alert>}
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy} disabled={!v.name.trim() || !v.hireDate}>Guardar</Button></div>
      </Form>
      {employee && <VacationAdjustments employeeId={employee.id} onChanged={balance.reload} />}
    </Modal>
  );
}

function VacationAdjustments({ employeeId, onChanged }: { employeeId: number; onChanged: () => void }) {
  const list = useAsync(() => payrollApi.vacationAdjustments(employeeId), [employeeId]);
  const [v, setV] = useState({ date: todayIso(), days: 0, reason: '' });
  const { run, busy } = useAction();
  return (
    <Card title="Ajustes de vacaciones">
      <Toolbar>
        <Input type="date" value={v.date} onChange={(x) => setV({ ...v, date: x })} />
        <NumberInput value={v.days} onChange={(x) => setV({ ...v, days: x })} />
        <Input value={v.reason} onChange={(x) => setV({ ...v, reason: x })} placeholder="Motivo (p. ej. saldo migrado, días disfrutados)" maxLength={200} />
        <Button busy={busy} disabled={v.days === 0 || v.reason.trim().length < 3} onClick={() => run(() => payrollApi.addVacationAdjustment(employeeId, { ...v, reason: v.reason.trim() }), { success: 'Ajuste registrado.' }).then(() => { setV({ ...v, days: 0, reason: '' }); list.reload(); onChanged(); })}>Agregar</Button>
      </Toolbar>
      <Loadable state={list}>
        {(rows) => <Table rows={rows} rowKey={(a) => a.id} empty="Sin ajustes." columns={[
          { key: 'f', title: 'Fecha', render: (a) => fmtDate(a.date) },
          { key: 'd', title: 'Días', align: 'right', render: (a) => a.days },
          { key: 'm', title: 'Motivo', render: (a) => a.reason },
        ]} />}
      </Loadable>
      <Alert>Días positivos se descuentan del saldo (p. ej. vacaciones ya disfrutadas); negativos lo aumentan (p. ej. saldo que traía del sistema anterior).</Alert>
    </Card>
  );
}

// ================================================================ Planillas

function GroupsTab() {
  const list = useAsync(() => payrollApi.groups(), []);
  const [selected, setSelected] = useState<PayrollGroupDto | null>(null);
  const [v, setV] = useState({ name: '', frequency: 'QUINCENAL' });
  const { run, busy } = useAction();
  const frequencies = Object.entries(PAY_FREQUENCIES).map(([value, label]) => ({ value, label }));
  return (
    <>
      <Toolbar>
        <Input value={v.name} onChange={(x) => setV({ ...v, name: x })} placeholder="Nombre de la nueva planilla" maxLength={200} />
        <Select value={v.frequency} onChange={(x) => setV({ ...v, frequency: x })} options={frequencies} />
        <Button kind="primary" busy={busy} disabled={!v.name.trim()} onClick={() => run(() => payrollApi.createGroup({ name: v.name.trim(), frequency: v.frequency, status: 'A', employeeIds: [] }), { success: 'Planilla creada.' }).then(() => { setV({ ...v, name: '' }); list.reload(); })}>Crear</Button>
      </Toolbar>
      <Alert>La frecuencia de pago define cuántos pagos hay al año: con eso se calculan la renta y los topes de las cargas en cada pago.</Alert>
      <Loadable state={list}>
        {(rows) => <Table rows={rows} rowKey={(g) => g.id} selected={selected?.id} onRowClick={setSelected} columns={[
          { key: 'i', title: 'Número', render: (g) => g.id },
          { key: 'n', title: 'Nombre', render: (g) => g.name },
          { key: 'f', title: 'Frecuencia', render: (g) => (g.frequency ? PAY_FREQUENCIES[g.frequency] ?? g.frequency : <Badge tone="warn">Sin indicar</Badge>) },
          { key: 'e', title: 'Empleados', align: 'right', render: (g) => g.employees.length },
          { key: 's', title: 'Estado', render: (g) => <Badge tone={activeTone(g.status)}>{g.status === 'A' ? 'Activa' : 'Inactiva'}</Badge> },
        ]} />}
      </Loadable>
      {selected && <GroupDetail id={selected.id} onChanged={list.reload} />}
    </>
  );
}

function GroupDetail({ id, onChanged }: { id: number; onChanged: () => void }) {
  const g = useAsync(() => payrollApi.group(id), [id]);
  const [employeeId, setEmployeeId] = useState<number | null>(null);
  const { run, busy } = useAction();
  const reload = () => { g.reload(); onChanged(); };
  const frequencies = Object.entries(PAY_FREQUENCIES).map(([value, label]) => ({ value, label }));
  return (
    <Loadable state={g}>
      {(x) => (
        <Card title={`Planilla ${x.id} · ${x.name}`} actions={
          <Button busy={busy} disabled={!x.frequency} onClick={() => run(() => payrollApi.updateGroup(x.id, { name: x.name, frequency: x.frequency ?? 'QUINCENAL', status: x.status === 'A' ? 'I' : 'A' }), { success: 'Planilla actualizada.' }).then(reload)}>{x.status === 'A' ? 'Inactivar' : 'Activar'}</Button>
        }>
          <Toolbar>
            <Field label="Frecuencia de pago">
              <Select value={x.frequency ?? ''} placeholder="Elija" onChange={(f) => run(() => payrollApi.updateGroup(x.id, { name: x.name, frequency: f, status: x.status }), { success: 'Frecuencia guardada.' }).then(reload)} options={frequencies} />
            </Field>
            <EmployeeSelect value={employeeId} onChange={setEmployeeId} status="A" />
            <Button busy={busy} disabled={!employeeId} onClick={() => run(() => payrollApi.addGroupEmployee(x.id, employeeId!), { success: 'Empleado agregado.' }).then(reload)}>Agregar empleado</Button>
          </Toolbar>
          <Table rows={x.employees} rowKey={(e) => e.employeeId} empty="Sin empleados." columns={[
            { key: 'i', title: 'Número', render: (e) => e.employeeId },
            { key: 'n', title: 'Nombre', render: (e) => e.name },
            { key: 's', title: 'Estado', render: (e) => <Badge tone={activeTone(e.status)}>{e.status === 'A' ? 'Activo' : 'Inactivo'}</Badge> },
            { key: 'x', title: '', render: (e) => <Button kind="link" busy={busy} onClick={() => run(() => payrollApi.removeGroupEmployee(x.id, e.employeeId), { confirm: `¿Quitar a ${e.name} de la planilla?`, success: 'Empleado removido.' }).then(reload)}>Quitar</Button> },
          ]} />
        </Card>
      )}
    </Loadable>
  );
}

// ================================================================ Novedades

function TimeTab() {
  const [employeeId, setEmployeeId] = useState<number | null>(null);
  const list = useAsync(() => (employeeId ? payrollApi.timeRecords(employeeId) : Promise.resolve([] as TimeRecordDto[])), [employeeId]);
  const types = useAsync(() => payrollLegalApi.noveltyTypes(true), []);
  const [v, setV] = useState({ type: '', date: todayIso(), hours: 0, observation: '' });
  const { run, busy } = useAction();
  const typeOptions = (types.data ?? []).map((t) => ({ value: t.code, label: t.description }));
  const selectedType = (types.data ?? []).find((t) => t.code === v.type);
  return (
    <>
      <Toolbar>
        <EmployeeSelect value={employeeId} onChange={setEmployeeId} status="A" />
      </Toolbar>
      {employeeId && (
        <>
          <Toolbar>
            <Select value={v.type} onChange={(x) => setV({ ...v, type: x })} placeholder="Tipo de novedad" options={typeOptions} />
            <Input type="date" value={v.date} onChange={(x) => setV({ ...v, date: x })} />
            <NumberInput value={v.hours} onChange={(x) => setV({ ...v, hours: x })} min={0} />
            <Input value={v.observation} onChange={(x) => setV({ ...v, observation: x })} placeholder="Observación" maxLength={200} />
            <Button kind="primary" busy={busy} disabled={v.hours <= 0 || !v.type} onClick={() => run(() => payrollApi.createTimeRecord({ employeeId, ...v, observation: v.observation.trim() || null }), { success: 'Registro guardado.' }).then(list.reload)}>Agregar</Button>
          </Toolbar>
          {selectedType && <p><small>{selectedType.factor > 0 ? `Se paga cada hora × ${selectedType.factor}.` : selectedType.factor < 0 ? `Rebaja del salario ${Math.abs(selectedType.factor * 100)} % de esas horas.` : 'No cambia el salario.'}{selectedType.isVacation ? ' Cuenta como día de vacaciones.' : ''}{selectedType.note ? ` ${selectedType.note}` : ''}</small></p>}
          <Loadable state={list}>
            {(rows) => <Table rows={rows} rowKey={(r) => r.id} empty="Sin registros." columns={[
              { key: 'f', title: 'Fecha', render: (r) => fmtDate(r.date) },
              { key: 't', title: 'Tipo', render: (r) => r.typeName || r.type },
              { key: 'h', title: 'Horas', align: 'right', render: (r) => r.hours },
              { key: 'o', title: 'Observación', render: (r) => r.observation ?? '' },
              { key: 'g', title: 'Pagada en', render: (r) => (r.generationId ? `Generación ${r.generationId}` : 'Pendiente') },
              { key: 'e', title: 'Estado', render: (r) => <Badge tone={r.status === 'A' ? 'ok' : 'bad'}>{r.status === 'A' ? 'Activa' : 'Eliminada'}</Badge> },
              { key: 'x', title: '', render: (r) => (r.status === 'A' && !r.generationId ? <Button kind="link" busy={busy} onClick={() => run(() => payrollApi.deleteTimeRecord(r.id), { confirm: '¿Eliminar el registro?', success: 'Registro eliminado.' }).then(list.reload)}>Eliminar</Button> : null) },
            ]} />}
          </Loadable>
          <Alert>Un solo registro vigente por empleado y fecha. Los tipos y sus recargos se configuran en Parámetros de planilla → Tipos de novedad.</Alert>
        </>
      )}
    </>
  );
}

// ================================================================ Planilla ordinaria

function GenerateTab() {
  const country = useCountry();
  const [q, setQ] = useState({ groupId: null as number | null, from: firstOfMonthIso(), to: todayIso() });
  const preview = useAsync(() => payrollApi.preview(q.groupId!, q.from, q.to), [q.groupId, q.from, q.to], !!q.groupId);
  const [g, setG] = useState({ entryDate: todayIso(), templateCode: null as number | null });
  const [detail, setDetail] = useState<PayrollLineDto | null>(null);
  const { run, busy } = useAction();
  return (
    <>
      <Toolbar>
        <GroupSelect value={q.groupId} onChange={(x) => setQ({ ...q, groupId: x })} />
        <Field label="Desde"><Input type="date" value={q.from} onChange={(x) => setQ({ ...q, from: x })} /></Field>
        <Field label="Hasta"><Input type="date" value={q.to} onChange={(x) => setQ({ ...q, to: x })} /></Field>
      </Toolbar>
      {!q.groupId && <Alert>Elija la planilla y el período para ver el cálculo antes de generarla.</Alert>}
      {q.groupId && (
        <Loadable state={preview}>
          {(p) => (
            <>
              {p.warnings.map((w) => <Alert key={w} kind="warn">{w}</Alert>)}
              <div className="acc-stats">
                <Stat label="Bruto" value={fmtMoney(p.totalGross)} />
                <Stat label={`Cargas del empleado (${country?.socialSecurity ?? ''})`} value={fmtMoney(p.totalEmployeeDeduction)} />
                <Stat label={country?.incomeTax ?? 'Renta'} value={fmtMoney(p.totalIncomeTax)} />
                <Stat label="Neto a pagar" value={fmtMoney(p.totalNet)} />
                <Stat label="Cargas patronales" value={fmtMoney(p.totalEmployerContribution)} />
                <Stat label="Vacaciones" value={fmtMoney(p.totalVacation)} />
              </div>
              <p><small>{p.periodsPerYear} pagos al año · haga clic en un empleado para ver su desglose.</small></p>
              <Table rows={p.lines} rowKey={(l) => l.employeeId} onRowClick={setDetail} empty="Sin empleados activos en el período." columns={[
                { key: 'n', title: 'Empleado', render: (l) => l.name },
                money<PayrollLineDto>('b', 'Salario', (l) => l.baseSalary),
                money<PayrollLineDto>('x', 'Novedades', (l) => l.grossSalary - l.baseSalary),
                money<PayrollLineDto>('g', 'Bruto', (l) => l.grossSalary),
                money<PayrollLineDto>('de', 'Cargas empleado', (l) => l.employeeDeduction),
                money<PayrollLineDto>('r', 'Renta', (l) => l.incomeTax),
                money<PayrollLineDto>('ne', 'Neto', (l) => l.net),
                money<PayrollLineDto>('ap', 'Cargas patronales', (l) => l.employerContribution),
                money<PayrollLineDto>('v', 'Vacaciones', (l) => l.vacation),
              ]} />
              <Card title="Totales por carga social"><ContributionsTable rows={p.contributionTotals} /></Card>
              <Toolbar>
                <Field label="Fecha del asiento"><Input type="date" value={g.entryDate} onChange={(x) => setG({ ...g, entryDate: x })} /></Field>
                <Field label="Plantilla" hint="Vacío = la asignada en Integración"><TemplateSelect moduleCode="PL" value={g.templateCode} onChange={(x) => setG({ ...g, templateCode: x })} /></Field>
                <Button kind="primary" busy={busy} disabled={p.lines.length === 0} onClick={() => run(() => payrollApi.generate({ payrollGroupId: q.groupId!, dateFrom: q.from, dateTo: q.to, ...g }), {
                  confirm: `¿Generar la planilla con ${p.lines.length} empleado(s) por un neto de ${fmtMoney(p.totalNet)}?`, success: 'Planilla generada con su asiento.',
                }).then((r) => { if (r) preview.reload(); })}>Generar</Button>
              </Toolbar>
              <Alert>El asiento lleva: salario, vacaciones, neto, renta y las cargas (en total o una línea por cada carga, según su plantilla). Debe cuadrar.</Alert>
            </>
          )}
        </Loadable>
      )}
      {detail && <PayrollLineDetail line={detail} onClose={() => setDetail(null)} />}
    </>
  );
}

function PayrollLineDetail({ line, onClose }: { line: PayrollLineDto; onClose: () => void }) {
  return (
    <Modal title={`Desglose: ${line.name}`} onClose={onClose} size="lg">
      <div className="acc-stats">
        <Stat label="Horas × salario hora" value={`${line.periodHours} × ${fmtMoney(line.hourlyRate)}`} />
        <Stat label="Salario" value={fmtMoney(line.baseSalary)} />
        <Stat label="Bruto" value={fmtMoney(line.grossSalary)} />
        <Stat label="Renta" value={fmtMoney(line.incomeTax)} />
        <Stat label="Neto" value={fmtMoney(line.net)} />
      </div>
      <Card title="Novedades">
        <Table rows={line.novelties} rowKey={(n) => n.code} empty="Sin novedades en el período." columns={[
          { key: 'd', title: 'Tipo', render: (n) => n.description },
          { key: 'h', title: 'Horas', align: 'right', render: (n) => n.hours },
          { key: 'f', title: 'Factor', align: 'right', render: (n) => n.factor },
          money<(typeof line.novelties)[number]>('m', 'Monto', (n) => n.amount),
        ]} />
      </Card>
      <Card title="Cargas sociales"><ContributionsTable rows={line.contributions} /></Card>
      <div className="acc-actions"><Button onClick={onClose}>Cerrar</Button></div>
    </Modal>
  );
}

// ================================================================ Bonos de ley

function BonusTab() {
  const country = useCountry();
  const bonuses = useAsync(() => payrollLegalApi.bonuses(), []);
  const [q, setQ] = useState({ code: '', year: new Date().getFullYear(), groupId: null as number | null });
  const active = (bonuses.data ?? []).filter((b) => b.status === 'A');
  const code = q.code || active[0]?.code || '';
  const preview = useAsync(() => payrollApi.bonusPreview({ bonusCode: code || undefined, year: q.year, payrollGroupId: q.groupId ?? undefined }), [code, q.year, q.groupId], !!code);
  const [g, setG] = useState({ entryDate: todayIso(), templateCode: null as number | null });
  const { run, busy } = useAction();
  return (
    <>
      <Toolbar>
        <Field label={country?.bonusName ?? 'Bono'}><Select value={code} onChange={(x) => setQ({ ...q, code: x })} options={active.map((b) => ({ value: b.code, label: b.description }))} /></Field>
        <Field label="Año en que termina el período"><NumberInput step="1" value={q.year} onChange={(x) => setQ({ ...q, year: Math.round(x) })} /></Field>
        <GroupSelect value={q.groupId} onChange={(x) => setQ({ ...q, groupId: x || null })} allowAll />
      </Toolbar>
      {bonuses.data && active.length === 0 && <Alert kind="warn">No hay bonos de ley activos. Revíselos en <Link to="/payroll-legal">Parámetros de planilla</Link>.</Alert>}
      {code && (
        <Loadable state={preview}>
          {(p) => (
            <>
              <Alert>{p.description}: período {fmtDate(p.dateFrom)} – {fmtDate(p.dateTo)}. Base: {p.salaryBasis === 'BRUTO' ? 'salarios brutos pagados (incluye horas extra)' : 'salario ordinario'}.</Alert>
              {p.warnings.map((w) => <Alert key={w} kind="warn">{w}</Alert>)}
              <Table rows={p.lines} rowKey={(l) => l.employeeId} empty="Sin salarios pagados en el período." columns={[
                { key: 'n', title: 'Empleado', render: (l) => l.name },
                money<BonusLineDto>('s', 'Salarios del período', (l) => l.salaryBase),
                money<BonusLineDto>('a', 'Bono', (l) => l.amount),
                money<BonusLineDto>('d', 'Cargas empleado', (l) => l.employeeDeduction),
                money<BonusLineDto>('r', 'Renta', (l) => l.incomeTax),
                money<BonusLineDto>('ne', 'Neto', (l) => l.net),
                money<BonusLineDto>('p', 'Cargas patronales', (l) => l.employerContribution),
                { key: 'e', title: 'Estado', render: (l) => (p.alreadyGenerated.includes(l.employeeId) ? <Badge tone="ok">Pagado</Badge> : <Badge tone="warn">Pendiente</Badge>) },
              ]} />
              <Toolbar>
                <Field label="Fecha del asiento"><Input type="date" value={g.entryDate} onChange={(x) => setG({ ...g, entryDate: x })} /></Field>
                <Field label="Plantilla" hint="Vacío = la asignada en Integración"><TemplateSelect moduleCode="PL" value={g.templateCode} onChange={(x) => setG({ ...g, templateCode: x })} /></Field>
                <Button kind="primary" busy={busy} onClick={() => run(() => payrollApi.bonusGenerate({ bonusCode: code, year: q.year, payrollGroupId: q.groupId ?? undefined, ...g }), { confirm: `¿Generar ${p.description} de los empleados pendientes?`, success: 'Bono generado.' }).then(() => preview.reload())}>Generar</Button>
              </Toolbar>
            </>
          )}
        </Loadable>
      )}
    </>
  );
}

// ================================================================ Liquidación

function SettlementTab() {
  const [q, setQ] = useState({ employeeId: null as number | null, noticeDays: null as number | null, includeSeverance: true, includeNotice: true, asOf: '', incomeTax: 0 });
  const query = { employeeId: q.employeeId!, noticeDays: q.noticeDays ?? undefined, includeSeverance: q.includeSeverance, includeNotice: q.includeNotice, asOf: q.asOf || undefined, incomeTaxWithholding: q.incomeTax || undefined };
  const preview = useAsync(() => payrollApi.settlementPreview(query), [q], !!q.employeeId);
  const [g, setG] = useState({ entryDate: todayIso(), templateCode: null as number | null });
  const { run, busy } = useAction();
  return (
    <>
      <Toolbar>
        <EmployeeSelect value={q.employeeId} onChange={(x) => setQ({ ...q, employeeId: x })} status="I" />
        <Field label="Corte" hint="Vacío = fecha de salida"><Input type="date" value={q.asOf} onChange={(x) => setQ({ ...q, asOf: x })} /></Field>
        <Check checked={q.includeSeverance} onChange={(x) => setQ({ ...q, includeSeverance: x })} label="Despido sin justa causa (cesantía / indemnización)" />
        <Check checked={q.includeNotice} onChange={(x) => setQ({ ...q, includeNotice: x })} label="Pagar preaviso" />
        <Field label="Renta a retener" hint="0 = no se retiene"><NumberInput value={q.incomeTax || null} min={0} onChange={(x) => setQ({ ...q, incomeTax: x || 0 })} /></Field>
        {q.includeNotice && <Field label="Días de preaviso" hint="Vacío = según la ley"><Input type="number" value={q.noticeDays ?? ''} onChange={(x) => setQ({ ...q, noticeDays: x === '' ? null : Math.max(0, Math.round(Number(x))) })} /></Field>}
      </Toolbar>
      {!q.employeeId && <Alert>Sólo aparecen empleados inactivos. Para liquidar a alguien, primero indique su fecha de salida e inactívelo en Empleados.</Alert>}
      {q.employeeId && (
        <Loadable state={preview}>
          {(p) => (
            <>
              <div className="acc-stats">
                <Stat label="Antigüedad" value={`${p.yearsWorked.toFixed(2)} años (${p.monthsWorked} meses)`} />
                <Stat label="Salario mensual promedio" value={fmtMoney(p.averageMonthly)} />
                <Stat label="Salario diario" value={fmtMoney(p.dailyAmount)} />
                <Stat label="Total" value={fmtMoney(p.total)} />
                <Stat label="Cargas del empleado" value={fmtMoney(p.employeeDeduction ?? 0)} />
                <Stat label="Renta" value={fmtMoney(p.incomeTax ?? 0)} />
                <Stat label="Neto a pagar" value={fmtMoney(p.net ?? p.total)} tone="pos" />
              </div>
              <Table rows={p.items} rowKey={(i) => `${i.code}-${i.description}`} columns={[
                { key: 'd', title: 'Concepto', render: (i) => i.description },
                { key: 'q', title: 'Cantidad', align: 'right', render: (i) => (i.unit ? `${i.quantity} ${i.unit}` : '') },
                money<SettlementItemDto>('u', 'Valor unidad', (i) => (i.unit ? i.unitValue : null)),
                money<SettlementItemDto>('m', 'Monto', (i) => i.amount),
              ]} />
              {(p.contributions ?? []).length > 0 && (
                <Card title="Cargas sociales de la liquidación">
                  <Table rows={p.contributions} rowKey={(c) => `${c.code}-${c.appliesTo}`} columns={[
                    { key: 'd', title: 'Carga', render: (c) => c.description },
                    { key: 'a', title: 'Paga', render: (c) => (c.appliesTo === 'EMPLEADO' ? 'Empleado' : 'Patrono') },
                    money<PayrollContributionLineDto>('b', 'Base', (c) => c.base),
                    { key: 'p', title: '%', align: 'right', render: (c) => `${c.percentage}` },
                    money<PayrollContributionLineDto>('m', 'Monto', (c) => c.amount),
                  ]} />
                </Card>
              )}
              {p.notes.map((n) => <Alert key={n} kind="warn">{n}</Alert>)}
              <Toolbar>
                <Field label="Fecha del asiento"><Input type="date" value={g.entryDate} onChange={(x) => setG({ ...g, entryDate: x })} /></Field>
                <Field label="Plantilla" hint="Vacío = la asignada en Integración"><TemplateSelect moduleCode="PL" value={g.templateCode} onChange={(x) => setG({ ...g, templateCode: x })} /></Field>
                <Button kind="primary" busy={busy} disabled={p.total <= 0} onClick={() => run(() => payrollApi.settlementGenerate({ ...query, ...g }), { confirm: `¿Generar la liquidación de ${p.name}: total ${fmtMoney(p.total)}, neto a pagar ${fmtMoney(p.net ?? p.total)}?`, success: 'Liquidación generada.' }).then(() => preview.reload())}>Generar</Button>
              </Toolbar>
            </>
          )}
        </Loadable>
      )}
    </>
  );
}

// ================================================================ Generaciones

function GenerationsTab() {
  const [q, setQ] = useState({ type: '', status: '', dateFrom: '', dateTo: '' });
  const list = useAsync(() => payrollApi.generations({ type: q.type || undefined, status: q.status || undefined, dateFrom: q.dateFrom || undefined, dateTo: q.dateTo || undefined }), [q]);
  const [detail, setDetail] = useState<number | null>(null);
  return (
    <>
      <Toolbar>
        <Select value={q.type} onChange={(x) => setQ({ ...q, type: x })} placeholder="Todos los tipos" options={Object.entries(GENERATION_TYPES).map(([value, label]) => ({ value, label }))} />
        <Select value={q.status} onChange={(x) => setQ({ ...q, status: x })} placeholder="Todos" options={[{ value: 'A', label: 'Aplicadas' }, { value: 'N', label: 'Anuladas' }]} />
        <Field label="Desde"><Input type="date" value={q.dateFrom} onChange={(x) => setQ({ ...q, dateFrom: x })} /></Field>
        <Field label="Hasta"><Input type="date" value={q.dateTo} onChange={(x) => setQ({ ...q, dateTo: x })} /></Field>
      </Toolbar>
      <Loadable state={list}>
        {(rows) => <Table rows={rows} rowKey={(g) => g.id} onRowClick={(g) => setDetail(g.id)} columns={[
          { key: 'i', title: 'Número', render: (g) => g.id },
          { key: 't', title: 'Tipo', render: (g) => GENERATION_TYPES[g.type] ?? g.type },
          { key: 'p', title: 'Planilla', render: (g) => g.payrollGroupName ?? '' },
          { key: 'r', title: 'Período', render: (g) => `${fmtDate(g.dateFrom)} – ${fmtDate(g.dateTo)}` },
          { key: 'e', title: 'Empleados', align: 'right', render: (g) => g.employees },
          money<GenerationDto>('n', 'Neto', (g) => g.totalNet),
          { key: 'a', title: 'Asiento', render: (g) => g.entryCode },
          { key: 's', title: 'Estado', render: (g) => <Badge tone={g.status === 'A' ? 'ok' : 'bad'}>{g.status === 'A' ? 'Aplicada' : 'Anulada'}</Badge> },
        ]} />}
      </Loadable>
      {detail !== null && <GenerationDetail id={detail} onClose={() => setDetail(null)} onChanged={list.reload} />}
    </>
  );
}

const CONCEPT_TYPES: Record<string, string> = { N: 'Novedad', C: 'Carga social', R: 'Renta' };

function GenerationDetail({ id, onClose, onChanged }: { id: number; onClose: () => void; onChanged: () => void }) {
  const g = useAsync(() => payrollApi.generation(id), [id]);
  const [employee, setEmployee] = useState<GenerationDetailDto | null>(null);
  const { run, busy } = useAction();
  return (
    <Modal title={`Generación ${id}`} onClose={onClose} size="lg">
      <Loadable state={g}>
        {(x) => (
          <>
            <div className="acc-stats">
              <Stat label="Tipo" value={GENERATION_TYPES[x.type] ?? x.type} />
              <Stat label="Período" value={`${fmtDate(x.dateFrom)} – ${fmtDate(x.dateTo)}`} />
              <Stat label="Bruto" value={fmtMoney(x.totalGross)} />
              <Stat label="Renta" value={fmtMoney(x.totalIncomeTax)} />
              <Stat label="Neto" value={fmtMoney(x.totalNet)} />
              <Stat label="Asiento" value={`${x.entryCode}${x.cancellationEntryCode ? ` · reverso ${x.cancellationEntryCode}` : ''}`} />
            </div>
            {x.cancellationReason && <Alert kind="warn">Anulada: {x.cancellationReason}</Alert>}
            <Table rows={x.details} rowKey={(d) => d.employeeId} onRowClick={setEmployee} columns={[
              { key: 'n', title: 'Empleado', render: (d) => d.name },
              money<GenerationDetailDto>('g', 'Bruto', (d) => d.grossSalary),
              money<GenerationDetailDto>('de', 'Cargas empleado', (d) => d.employeeDeduction),
              money<GenerationDetailDto>('r', 'Renta', (d) => d.incomeTax),
              money<GenerationDetailDto>('ap', 'Cargas patronales', (d) => d.employerContribution),
              money<GenerationDetailDto>('v', 'Vacaciones', (d) => d.vacation),
              money<GenerationDetailDto>('ag', 'Bono', (d) => d.christmasBonus),
              money<GenerationDetailDto>('c', 'Cesantía', (d) => d.severance),
              money<GenerationDetailDto>('pr', 'Prima', (d) => d.seniorityPremium),
              money<GenerationDetailDto>('p', 'Preaviso', (d) => d.notice),
              money<GenerationDetailDto>('ne', 'Neto', (d) => d.net),
            ]} />
            {employee && (
              <Card title={`Desglose de ${employee.name}`}>
                <Table rows={employee.concepts} rowKey={(c) => `${c.type}-${c.code}`} empty="Sin desglose guardado." columns={[
                  { key: 't', title: 'Tipo', render: (c) => CONCEPT_TYPES[c.type] ?? c.type },
                  { key: 'd', title: 'Concepto', render: (c) => c.description },
                  { key: 'a', title: 'Paga', render: (c) => (c.appliesTo === 'PATRONO' ? 'Patrono' : c.appliesTo === 'EMPLEADO' ? 'Empleado' : '') },
                  { key: 'h', title: 'Horas', align: 'right', render: (c) => c.hours ?? '' },
                  { key: 'p', title: 'Tasa / factor', align: 'right', render: (c) => (c.percentage == null ? '' : c.type === 'N' ? c.percentage : pct(c.percentage)) },
                  money<GenerationConceptDto>('b', 'Base', (c) => c.base),
                  money<GenerationConceptDto>('m', 'Monto', (c) => c.amount),
                ]} />
              </Card>
            )}
            <div className="acc-actions">
              {x.status === 'A' && <Button kind="danger" busy={busy} onClick={() => run((reason) => payrollApi.cancelGeneration(x.id, reason ?? ''), {
                confirm: `¿Anular la generación ${x.id}? Se reversa su asiento${x.type === 'ORD' ? ' y sus novedades vuelven a quedar pendientes' : ''}.`, danger: true, requireReason: true, success: 'Generación anulada.',
              }).then(() => { g.reload(); onChanged(); })}>Anular</Button>}
              <Button onClick={onClose}>Cerrar</Button>
            </div>
          </>
        )}
      </Loadable>
    </Modal>
  );
}

// ================================================================ Reporte mensual para la seguridad social

const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Setiembre', 'Octubre', 'Noviembre', 'Diciembre'];

function MonthlyReportTab() {
  const country = useCountry();
  const now = new Date();
  const [q, setQ] = useState({ year: now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear(), month: now.getMonth() === 0 ? 12 : now.getMonth() });
  const report = useAsync(() => payrollReportsApi.monthly(q.year, q.month), [q.year, q.month]);
  const { run, busy } = useAction();
  return (
    <>
      <Toolbar>
        <Field label="Año"><NumberInput step="1" value={q.year} onChange={(x) => setQ({ ...q, year: x || now.getFullYear() })} /></Field>
        <Field label="Mes"><Select value={q.month} onChange={(x) => setQ({ ...q, month: x })} options={MONTH_NAMES.map((m, i) => ({ value: i + 1, label: m }))} /></Field>
        <Button busy={busy} onClick={() => run(() => payrollReportsApi.exportMonthly(q.year, q.month))}>
          {report.data?.layout === 'SIPE' ? 'Exportar columnas del SIPE (CSV)' : 'Exportar a Excel (CSV)'}
        </Button>
      </Toolbar>
      <Loadable state={report}>
        {(r) => (
          <>
            <Table<PayrollMonthlyRowDto> rows={r.rows} rowKey={(x) => x.employeeId} empty="No hay planillas en ese mes." columns={[
              { key: 'n', title: 'Empleado', render: (x) => <>{x.name}<div className="acc-muted">{x.identification ?? ''}{x.socialSecurityNumber ? ` · SS ${x.socialSecurityNumber}` : ''}</div></> },
              money<PayrollMonthlyRowDto>('o', 'Salario ordinario', (x) => x.ordinarySalary),
              money<PayrollMonthlyRowDto>('h', 'Horas extras', (x) => x.overtime || null),
              money<PayrollMonthlyRowDto>('v', 'Vacaciones', (x) => x.vacation || null),
              money<PayrollMonthlyRowDto>('b', country?.bonusName ?? 'Bonos de ley', (x) => x.bonus || null),
              money<PayrollMonthlyRowDto>('l', 'Liquidación', (x) => x.notice + x.severance + x.seniorityPremium || null),
              money<PayrollMonthlyRowDto>('cb', 'Base de cargas', (x) => x.contributionBase),
              money<PayrollMonthlyRowDto>('ce', 'Cargas empleado', (x) => x.employeeDeduction),
              money<PayrollMonthlyRowDto>('cp', 'Cargas patrono', (x) => x.employerContribution),
              money<PayrollMonthlyRowDto>('r', 'Renta', (x) => x.incomeTax || null),
            ]} />
            {r.notes.map((n) => <Alert key={n} kind={n.includes('no tienen') ? 'warn' : 'info'}>{n}</Alert>)}
          </>
        )}
      </Loadable>
    </>
  );
}
